export type BackgroundShade = 'white' | 'neutral-50' | 'neutral-100'

export type SectionPadding = 'md' | 'lg'

export type SectionSettings = {
  anchorId?: string | null
  backgroundShade?: BackgroundShade | null
  isDarkMode?: boolean | null
  padding?: SectionPadding | null
}

export type NormalizedSectionSettings = {
  anchorId?: string
  backgroundShade?: BackgroundShade
  isDarkMode?: boolean
  padding?: SectionPadding
}

/**
 * Payload returns `string | null` for optional fields; SectionWrapper and
 * downstream consumers want `string | undefined`. This strips the nulls
 * so every block doesn't re-do the conversion.
 */
export function normalizeSectionSettings(
  settings?: SectionSettings | null,
): NormalizedSectionSettings | undefined {
  if (!settings) return undefined
  return {
    anchorId: settings.anchorId ?? undefined,
    backgroundShade: settings.backgroundShade ?? undefined,
    isDarkMode: settings.isDarkMode ?? undefined,
    padding: settings.padding ?? undefined,
  }
}

/**
 * Selects the dark-mode asset variant when the section is in dark mode and a
 * dark variant exists; falls back to the light-mode variant otherwise. Used
 * by blocks with optional per-asset dark overrides (partner logos, card
 * logos, CTA images).
 */
export function pickDarkVariant<T>(
  light: T,
  dark: T | null | undefined,
  isDarkMode: boolean | null | undefined,
): T {
  return isDarkMode && dark ? dark : light
}

export function getBackgroundFromShade(shade?: BackgroundShade | null): 'white' | 'light-gray' {
  if (!shade || shade === 'white') return 'white'
  if (shade === 'neutral-50' || shade === 'neutral-100') return 'light-gray'
  return 'white'
}

export function getEffectiveBackground(
  settings?: SectionSettings | null,
): 'white' | 'light-gray' | 'dark' {
  if (settings?.isDarkMode) return 'dark'
  return getBackgroundFromShade(settings?.backgroundShade)
}

export function getSectionProps(s?: SectionSettings | null) {
  return {
    id: s?.anchorId || undefined,
    'data-theme': s?.isDarkMode ? ('dark' as const) : undefined,
  }
}

/**
 * Returns a stable key identifying the visual shade of a block's section so
 * consecutive blocks with the same shade can be detected with a CSS
 * adjacent-sibling selector and collapse their shared boundary padding.
 */
export function getSectionShadeKey(block: unknown): string {
  if (!block || typeof block !== 'object') return 'none'
  const settings = (block as { settings?: SectionSettings }).settings
  if (settings?.isDarkMode) return 'dark'
  return settings?.backgroundShade || 'white'
}
